import React, { useEffect, useState } from 'react';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import Button from '~~elements/Button';
import Form from '~~elements/Form';
import Multilingual from '~~utils/Multilingual';
import { SeasonResource } from '~~apis/resource';
import { SectionDiv } from '../styled';
import {
  tournamentTypes,
  tournamentTeamTypes,
  tournamentPlayerTypes,
  tournamentBallTypes,
  tournamentBatTypes,
} from '../Const';
import { useTournamentDispatch, useTournamentState } from '../useTournament';
import moment from 'moment';

const Basic = ({ tournament }) => {
  const tournamentDispatch = useTournamentDispatch();
  const { organizerUniqid, locations } = useTournamentState();
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [tournamentData, setTournamentData] = useState(null);

  useEffect(() => {
    const startAt = new Date(tournament.start_at);
    const endAt = new Date(tournament.end_at);
    setTournamentData({
      ...JSON.parse(JSON.stringify(tournament)),
      locations: !tournament.locations ? [] : tournament.locations.map((l) => l.uniqid),
      start_at: !!tournament.start_at && moment(startAt).isValid() ? moment(startAt).format('YYYY-MM-DD') : '',
      end_at: !!tournament.end_at && moment(endAt).isValid() ? moment(endAt).format('YYYY-MM-DD') : '',
    });
  }, [tournament]);

  const handleInputOnChange = (e) => {
    setErrorMsg('');
    setSuccessMsg('');

    if (e.target.name === 'auto_game_seq') {
      setTournamentData({
        ...tournamentData,
        auto_game_seq: e.target.checked,
      });
      return;
    }

    setTournamentData({
      ...tournamentData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLocationOnChange = (index) => (e) => {
    tournamentData.locations[index] = e.target.value;
    setTournamentData({
      ...tournamentData,
      locations: [...tournamentData.locations],
    });
  };

  const handleLocationRemove = (index) => (e) => {
    e.preventDefault();
    const newLocations = [...tournamentData.locations];
    newLocations.splice(index, 1);
    setTournamentData({
      ...tournamentData,
      locations: newLocations,
    });
  };

  const handleLocationCreate = (e) => {
    e.preventDefault();
    setTournamentData({
      ...tournamentData,
      locations: [...tournamentData.locations, ''],
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    const title = tournamentData.title.trim();
    if (title === '') {
      setErrorMsg(Multilingual(`TOURNAMENT.ERROR.TITLE`));
      return;
    }

    const startAt = new Date(tournamentData.start_at);
    const endAt = new Date(tournamentData.end_at);
    if (!moment(startAt).isValid() || !moment(endAt).isValid() || moment(startAt).isAfter(endAt)) {
      setErrorMsg(Multilingual(`TOURNAMENT.ERROR.DATES`));
      return;
    }

    SeasonResource.updateSeasonBasic({
      leagueUniqid: organizerUniqid,
      seasonUniqid: tournament.uniqid,
      data: {
        title,
        type: tournamentData.type,
        team_type: tournamentData.team_type,
        player_type: tournamentData.player_type,
        ball_type: tournamentData.ball_type,
        bat_type: tournamentData.bat_type,
        start_at: moment(startAt).format('YYYY/MM/DD'),
        end_at: moment(endAt).isValid() ? moment(endAt).format('YYYY/MM/DD') : '',
        auto_game_seq: tournamentData.auto_game_seq,
        locations: tournamentData.locations,
      },
    })
      .then(({ data }) => {
        tournamentDispatch({ type: 'SET_TOURNAMENT_INFO', payload: data });
        setSuccessMsg(Multilingual(`TOURNAMENT.SUCCESS.UPDATE`));
      })
      .catch(() => {
        setErrorMsg(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`));
      });
  };

  if (!tournamentData) {
    return <SectionDiv></SectionDiv>;
  }

  return (
    <SectionDiv>
      <Form onSubmit={handleSubmit}>
        <div className="box">
          {/* 賽事名稱 */}
          <label>{Multilingual('TOURNAMENT.TITLE')}</label>
          <div className="input-field">
            <div className="input-box">
              <Input
                width="100%"
                placeholder={Multilingual('TOURNAMENT.TITLE')}
                name="title"
                value={tournamentData.title}
                onChange={handleInputOnChange}
              />
            </div>
          </div>
          {/* 賽事型態 */}
          <label>{Multilingual('TOURNAMENT.TYPE')}</label>
          <div className="input-field">
            <Select width="100%" name="type" value={tournamentData.type} onChange={handleInputOnChange}>
              {tournamentTypes.map((tournamentType) => (
                <option key={tournamentType} value={tournamentType}>
                  {Multilingual(`TOURNAMENT.TYPE_${tournamentType}`)}
                </option>
              ))}
            </Select>
          </div>
          <div className="row --multi-input-field">
            {/* 球隊型態 */}
            <div>
              <label>{Multilingual('TOURNAMENT.TEAM_TYPE.WORDING')}</label>
              <div className="input-field">
                <Select width="100%" name="team_type" value={tournamentData.team_type} onChange={handleInputOnChange}>
                  {tournamentTeamTypes.map((tournamentTeamType) => (
                    <option key={tournamentTeamType} value={tournamentTeamType}>
                      {Multilingual(`TOURNAMENT.TEAM_TYPE.${tournamentTeamType}`)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            {/* 球員型態 */}
            <div>
              <label>{Multilingual('TOURNAMENT.PLAYER_TYPE.WORDING')}</label>
              <div className="input-field">
                <Select
                  width="100%"
                  name="player_type"
                  value={tournamentData.player_type}
                  onChange={handleInputOnChange}
                >
                  {tournamentPlayerTypes.map((tournamentPlayerType) => (
                    <option key={tournamentPlayerType} value={tournamentPlayerType}>
                      {Multilingual(`TOURNAMENT.PLAYER_TYPE.${tournamentPlayerType}`)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="row --multi-input-field">
            {/* 比賽用球 */}
            <div>
              <label>{Multilingual('TOURNAMENT.BALL_TYPE.WORDING')}</label>
              <div className="input-field">
                <Select width="100%" name="ball_type" value={tournamentData.ball_type} onChange={handleInputOnChange}>
                  {tournamentBallTypes.map((tournamentBallType) => (
                    <option key={tournamentBallType} value={tournamentBallType}>
                      {Multilingual(`TOURNAMENT.BALL_TYPE.${tournamentBallType}`)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            {/* 球棒要求 */}
            <div>
              <label>{Multilingual('TOURNAMENT.BAT_TYPE.WORDING')}</label>
              <div className="input-field">
                <Select width="100%" name="bat_type" value={tournamentData.bat_type} onChange={handleInputOnChange}>
                  {tournamentBatTypes.map((tournamentBatType) => (
                    <option key={tournamentBatType} value={tournamentBatType}>
                      {Multilingual(`TOURNAMENT.BAT_TYPE.${tournamentBatType}`)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          {/* 賽事期間 */}
          <label>{Multilingual('TOURNAMENT.DATES')}</label>
          <div className="input-field --multi-input">
            <div className="input-box">
              <Input
                width="100%"
                type="date"
                name="start_at"
                value={tournamentData.start_at}
                onChange={handleInputOnChange}
              />
              {` ~ `}
              <Input
                width="100%"
                type="date"
                name="end_at"
                value={tournamentData.end_at}
                onChange={handleInputOnChange}
              />
            </div>
          </div>
          {/* 賽事自動編號 */}
          <label>
            <input
              type="checkbox"
              name="auto_game_seq"
              checked={tournamentData.auto_game_seq}
              onChange={handleInputOnChange}
            />
            {Multilingual('TOURNAMENT.AUTO_GAME_SEQ_DESCRIPTION')}
          </label>
          <div className="input-field" />
          {/* 賽事使用球場 */}
          <label>{Multilingual('TOURNAMENT.SEASON_LOCATION')}</label>
          <div className="input-field">
            <div className="input-list">
              {tournamentData.locations.map((locationUniqid, locationUniqidIndex) => (
                <label key={locationUniqidIndex} className="remove-icon-wrapper">
                  <Select
                    width="100%"
                    name="location"
                    value={locationUniqid}
                    onChange={handleLocationOnChange(locationUniqidIndex)}
                  >
                    <option value="">{Multilingual('FORM.PLEASE_SELECT')}</option>
                    {locations.map((location) => (
                      <option key={location.uniqid} value={location.uniqid}>
                        {`${location.city}｜${location.name}`}
                      </option>
                    ))}
                  </Select>
                  <Button width="unset" color="transparent" mini onClick={handleLocationRemove(locationUniqidIndex)}>
                    X
                  </Button>
                </label>
              ))}
              <label>
                <Button width="unset" color="transparentWithBorder" onClick={handleLocationCreate}>
                  {Multilingual(`COMMON.CREATE`)}
                </Button>
              </label>
            </div>
          </div>
          <div className="error-msg">{errorMsg}</div>
          <div className="success-msg">{successMsg}</div>
          <Button width="unset" color="red" nowrap type="submit">
            {Multilingual(`COMMON.UPDATE`)}
          </Button>
        </div>
      </Form>
    </SectionDiv>
  );
};

export default Basic;
